import { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';

/**
 * Single-Gate Premium Access System
 * 
 * Simple locked/unlocked model:
 * - Locked: User is browsing casually
 * - Unlocked: User has registered once today (24h access)
 * 
 * FREE CONTENT (always accessible):
 * - Hero, Amenities, Gallery, Neighbourhood, Overview
 * 
 * GATED CONTENT (requires registration):
 * - Exact Pricing, Floor Plans, Virtual Tour, Site Visit, Brochure Download, Exit Intent
 * 
 * SINGLE FORM: Name + Phone (mandatory)
 */

export interface LeadContact {
  name: string;
  email?: string;
  phone: string;
  leadId?: string;
  capturedAt: number;
}

export interface LeadGateContextValue {
  // Current lead state
  contact: LeadContact | null;
  isUnlocked: boolean;
  
  // Actions
  grantAccess: (data: { name: string; email?: string; phone: string; leadId?: string }) => void;
  clearAccess: () => void;
}

// Storage keys as specified
const STORAGE_KEY = 'nahar_access_granted';
const EXPIRY_KEY = 'nahar_access_expiry';
const CONTACT_KEY = 'nahar_contact_data';
// 0 = session-only unlock: the gate form shows again on every new visit,
// but an in-session unlock must never re-lock (guarded in checkExpiry below)
const ACCESS_DURATION_MS = 0;

const LeadGateContext = createContext<LeadGateContextValue | undefined>(undefined);

function getStoredAccess(): LeadContact | null {
  try {
    const granted = localStorage.getItem(STORAGE_KEY);
    const expiry = localStorage.getItem(EXPIRY_KEY);
    const contactData = localStorage.getItem(CONTACT_KEY);
    
    if (!granted || granted !== 'true' || !expiry || !contactData) {
      return null;
    }
    
    const expiryTime = parseInt(expiry, 10);
    if (Date.now() > expiryTime) {
      // Access expired - clear storage
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(EXPIRY_KEY);
      localStorage.removeItem(CONTACT_KEY);
      document.cookie = 'nahar_unlocked=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/';
      return null;
    }
    
    return JSON.parse(contactData) as LeadContact;
  } catch {
    return null;
  }
}

function storeAccess(contact: LeadContact): void {
  // Session-only unlock (ACCESS_DURATION_MS = 0): skip persistence entirely,
  // otherwise the immediately-expired entry would trip getStoredAccess()
  if (ACCESS_DURATION_MS <= 0) return;
  try {
    const expiry = Date.now() + ACCESS_DURATION_MS;
    localStorage.setItem(STORAGE_KEY, 'true');
    localStorage.setItem(EXPIRY_KEY, expiry.toString());
    localStorage.setItem(CONTACT_KEY, JSON.stringify(contact));
    
    // Also set a cookie for cross-subdomain persistence
    const expires = new Date(expiry);
    document.cookie = `nahar_unlocked=true;expires=${expires.toUTCString()};path=/;SameSite=Lax`;
  } catch {
    // Storage might be disabled
  }
}

function clearStoredAccess(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(EXPIRY_KEY);
    localStorage.removeItem(CONTACT_KEY);
    document.cookie = 'nahar_unlocked=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/';
  } catch {
    // Ignore
  }
}

export function LeadGateProvider({ children }: { children: ReactNode }) {
  const [contact, setContact] = useState<LeadContact | null>(() => getStoredAccess());
  // True once the visitor unlocks during this session — prevents the expiry
  // interval from re-locking content that was granted in-session
  const sessionUnlocked = useRef(false);

  // Check expiry on mount and periodically
  useEffect(() => {
    const checkExpiry = () => {
      // In-session unlock holds for as long as the tab stays open
      if (sessionUnlocked.current) return;
      const storedContact = getStoredAccess();
      if (!storedContact && contact) {
        setContact(null);
      }
    };
    
    // Check every minute
    const interval = setInterval(checkExpiry, 60000);
    return () => clearInterval(interval);
  }, [contact]);

  const isUnlocked = Boolean(contact);

  const grantAccess = useCallback((data: { name: string; email?: string; phone: string; leadId?: string }) => {
    const newContact: LeadContact = {
      name: data.name,
      email: data.email,
      phone: data.phone,
      leadId: data.leadId,
      capturedAt: Date.now(),
    };
    sessionUnlocked.current = true;
    setContact(newContact);
    storeAccess(newContact);
  }, []);

  const clearAccess = useCallback(() => {
    sessionUnlocked.current = false;
    setContact(null);
    clearStoredAccess();
  }, []);

  return (
    <LeadGateContext.Provider
      value={{
        contact,
        isUnlocked,
        grantAccess,
        clearAccess,
      }}
    >
      {children}
    </LeadGateContext.Provider>
  );
}

export function useLeadGate(): LeadGateContextValue {
  const context = useContext(LeadGateContext);
  if (!context) {
    throw new Error('useLeadGate must be used within a LeadGateProvider');
  }
  return context;
}

/**
 * List of gated actions that require registration
 * All use the same single registration form
 */
export const GATED_ACTIONS = [
  'price',
  'floorplan',
  'floor_plan',
  'callback',
  'site_visit',
  'virtual_tour',
  'brochure',
  'dossier',
  'exit_intent',
  'contact',
] as const;

export type GatedAction = typeof GATED_ACTIONS[number];

/**
 * Check if an action requires registration
 */
export function isGatedAction(action: string): boolean {
  return GATED_ACTIONS.includes(action as GatedAction);
}
